import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {FormGroup} from '@angular/forms';

import {MatSelectChange} from '@angular/material/select';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';

import {
    GenericCameraSettings,
    GenericCameraSettingsChild,
    InputType,
    CameraSettingsErrorStateMatcher,
    RenderType
} from '@pages/camera-settings/models';

interface CustomSteps {
    legend: number;
    value: number;
}

@Component({
    selector: 'mat-generic-form-field',
    templateUrl: './mat-generic-form-field.component.html',
    styleUrls: ['./mat-generic-form-field.component.scss'],
    host: {
        '(document:click)': 'closeInfo($event)'
    }
})
export class MatGenericFormFieldComponent implements OnInit {
    @ViewChild('customSliderRef') readonly customSliderRef!: ElementRef;
    @ViewChild('sliderLegendRef') readonly sliderLegendRef!: ElementRef;

    @Input() property: GenericCameraSettings;
    @Input() form!: FormGroup;
    @Output() onChange = new EventEmitter<any>();

    options: any;
    customSliderOption: {
        customSteps: CustomSteps[];
        maxSelectable: number;
        disabled?: boolean;
    };
    steps: CustomSteps[];

    draggingSlider: boolean = false;
    dragSliderBound: any;
    sliderReleaseBound: any;

    leftPosition = 4;
    maxLeftPosition = 0;

    customSliderDisabled: boolean = false;
    selectedCustomSliderValue: CustomSteps = {legend: 0, value: 0};

    childProperties: GenericCameraSettingsChild[] = [];
    matcher = new CameraSettingsErrorStateMatcher();

    hide = true;
    info_visible = false;

    constructor(private el: ElementRef) {}

    public get RenderType(): typeof RenderType {
        return RenderType;
    }

    public get InputType(): typeof InputType {
        return InputType;
    }

    get isValid() {
        try {
            if (this.form) {
                return this.form.controls[this.property.propertyName]?.valid;
            } else {
                return false;
            }
        } catch (e) {
            console.error(this.property.propertyName);
            throw e;
        }
    }

    get lastStep() {
        return this.customSliderOption.customSteps[
            this.customSliderOption.customSteps.length - 1
        ];
    }

    Error(err: string) {
        if (
            this.form &&
            this.form.controls[this.property.propertyName]?.errors
        ) {
            return this.form.controls[this.property.propertyName]?.errors[err];
        } else {
            return null;
        }
    }

    trackByIndex = (index: number): number => {
        return index;
    };

    onSliderClick(event: MouseEvent) {
        event.preventDefault();
        if (this.customSliderDisabled) return;

        this.draggingSlider = true;

        if (!this.dragSliderBound) {
            this.dragSliderBound = this.dragSlider.bind(this);
        }
        if (!this.sliderReleaseBound) {
            this.sliderReleaseBound = this.onSliderRelease.bind(this);
        }

        document.addEventListener('mousemove', this.dragSliderBound);
        document.addEventListener('mouseup', this.sliderReleaseBound);
        document.body.classList.add('pointer');
    }

    onSliderRelease(event: MouseEvent) {
        event.preventDefault();
        if (this.customSliderDisabled) return;

        if (this.draggingSlider) {
            this.draggingSlider = false;

            document.removeEventListener('mousemove', this.dragSliderBound);
            document.removeEventListener('mouseup', this.sliderReleaseBound);
            document.body.classList.remove('pointer');
        }
    }

    updateCustomSlider(event: MouseEvent) {
        this.sliderLegendRef.nativeElement.style.visibility = 'visible';

        const rect = this.customSliderRef.nativeElement.getBoundingClientRect();
        const clientPosition = Math.trunc(event.clientX - rect.left);

        const position =
            clientPosition <= 0
                ? 4
                : clientPosition > this.maxLeftPosition
                ? this.maxLeftPosition
                : clientPosition;

        const val = Math.trunc(position / 48);

        const average = (val * 48 + (val + 1) * 48) / 2;
        const newVal = position < average ? val : val + 1;

        const final = newVal * 48;

        this.leftPosition = final === 0 ? 4 : final + newVal * 4;
        this.selectedCustomSliderValue =
            this.customSliderOption.customSteps[newVal];

        this.customSliderUpdate();

        setTimeout(() => {
            if (!this.draggingSlider)
                this.sliderLegendRef.nativeElement.style.visibility = 'hidden';
        }, 300);
    }

    dragSlider(event: MouseEvent) {
        event.preventDefault();
        if (this.customSliderDisabled) return;

        if (this.draggingSlider) {
            this.updateCustomSlider(event);
        }
    }

    toggleInfo() {
        this.info_visible = !this.info_visible;
    }

    closeInfo(event: any) {
        const clickedInsideComponent = this.el.nativeElement.contains(
            event.target
        );

        if (!clickedInsideComponent) {
            if (this.info_visible) this.info_visible = false;
        }
    }

    onTrigger(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.hide = !this.hide;
    }

    textBoxUpdate($event: any) {
        this.property.value = $event.target.value;
        this.onChange.next($event.target.value);
    }

    dropdownUpdate($event: MatSelectChange) {
        this.property.value = $event.value;
        this.onChange.next($event.value);
    }

    toggleUpdate($event: MatSlideToggleChange) {
        this.property.value = $event.checked;
        this.onChange.next($event.checked);
    }

    checkBoxUpdate($event: any) {
        this.property.value = $event.checked;
        this.onChange.next($event.checked);
    }

    customSliderUpdate() {
        this.property.value = this.selectedCustomSliderValue.value;
        this.onChange.next(this.selectedCustomSliderValue.value);
    }

    ngOnInit(): void {
        if (this.property.renderType === RenderType.CustomSlider) {
            this.customSliderOption = {
                customSteps: this.property.customSteps,
                maxSelectable: this.property.maxSelectable,
                disabled: false
            };

            let count = 0;
            this.customSliderOption.customSteps.map((step) => {
                if (step.legend < this.property.maxSelectable) {
                    count++;
                }
            });

            this.steps = this.customSliderOption.customSteps.filter(
                (step) => step.value !== 0
            );
            this.maxLeftPosition = count * 48 + 8;
            this.customSliderDisabled = this.property.disabled;
        }

        if (this.property.renderType == RenderType.Slider) {
            if (
                this.property.stepsArray &&
                this.property.stepsArray.length > 0
            ) {
                this.options = {
                    showTicks: true,
                    stepsArray: this.property.stepsArray,
                    disabled: this.property.disabled
                };
            } else {
                this.options = {
                    floor: this.property.min,
                    ceil: this.property.max,
                    showTicks: true,
                    disabled: this.property.disabled
                };
            }
        }
    }

    ngOnChanges() {
        // * Updating disabled state
        if (this.property.disabled) {
            this.form.controls[this.property.propertyName]?.disable();
        } else {
            this.form.controls[this.property.propertyName]?.enable();
        }

        // * Updating customSteps for custom slider
        if (this.property.renderType === RenderType.CustomSlider) {
            this.customSliderOption = {
                customSteps: this.property.customSteps,
                maxSelectable: this.property.maxSelectable,
                disabled: false
            };

            let count = 0;
            this.customSliderOption.customSteps.map((step) => {
                if (step.legend < this.property.maxSelectable) {
                    count++;
                }
            });

            const val = this.property.value as number;

            this.leftPosition = val === 0 ? 4 : val * 48 + val * 4;
            this.customSliderDisabled = this.property.disabled;

            this.maxLeftPosition = count * 48 + 8;
            this.steps = this.customSliderOption.customSteps.filter(
                (step) => step.value !== 0
            );
        }

        // * Updating stepsArray for sliders
        if (this.property.renderType == RenderType.Slider) {
            if (
                this.property.stepsArray &&
                this.property.stepsArray.length > 0
            ) {
                this.options = {
                    showTicks: true,
                    stepsArray: this.property.stepsArray,
                    disabled: this.property.disabled
                };
            } else {
                this.options = {
                    floor: this.property.min,
                    ceil: this.property.max,
                    showTicks: true,
                    disabled: this.property.disabled
                };
            }
        }
    }

    ngOnDestroy(): void {
        document.removeEventListener('mousemove', this.dragSliderBound);
        document.removeEventListener('mouseup', this.sliderReleaseBound);
    }
}
